import { useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useLocation, useNavigate } from 'react-router-dom'
import clsx from 'clsx'

import { DatabasesAPI } from '@makyoapp/makyo-sdk'
import { IGenerateDatabaseBody } from '@makyoapp/makyo-sdk/api/Databases'
import { IDBAICollection } from '@makyoapp/makyo-sdk/models/Database-AI'
import { Check16, Info16, Trash16, Upload16 } from '@makyoapp/makyo-icon/icon'
import { MaterialIcon } from '@makyoapp/makyo-tw-ui'
import { Button, ButtonVariant, Field, Label, Modal, SelectDropdown, Slider } from '@makyoapp/makyo-ui'

import { AI_PROVIDERS } from '@/constants/ai-provider'
import { useCreateDbContext } from '@/context/CreateDbContext'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'
import { ORG_DATABASE_ROUTE, ORG_ROUTE } from '@/shared/constants'
import { SaveButton } from '@/components/SaveButton'

type EventProgressStatus = 'WORKING' | 'DONE' | 'ERROR'

type EventProgress = {
  eventName: string
  status: EventProgressStatus
  progress?: number
}

const STATUS_COLORS = {
  WORKING: 'bg-blue-500',
  DONE: 'bg-emerald-500',
  ERROR: 'bg-red-500',
} as const

const EVENT_PROGRESS_LABEL: Record<string, string> = {
  GET_SCHEMA: 'Fetching Structure',
  GET_DATA: 'Fetching & Processing Data',
  CREATE_DB: 'Creating Database',
  CREATE_TABLES: 'Building & Populating Tables',
  GET_SCREENS: 'Generating Screen Data',
}

function PromptToJson({ onCreateApp, onEnd }: { onCreateApp?: (dbId: string) => void; onEnd?: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const { currentOrg } = useCurrentOrgContext()
  const { setStep, step, value, reset, setShowModal, isLoading, setIsLoading } = useCreateDbContext()

  const isCreateNewApp = location.pathname.endsWith('add-new-app')

  const [newDB, setNewDB] = useState<string | undefined>(undefined)
  const [progressStatus, setProgressStatus] = useState<'INITIAL' | 'PROCESSING' | 'DONE' | 'ERROR'>('INITIAL')
  const [eventProgress, setEventProgress] = useState<Map<string, EventProgress>>(new Map())

  const [provider, setProvider] = useState(AI_PROVIDERS.gemini.value)
  const [model, setModel] = useState(AI_PROVIDERS.gemini.models[0])
  const [prompText, setPrompText] = useState('')
  const [generatedSchema, setGeneratedSchema] = useState<IDBAICollection[]>([])

  const [loadingSchema, setLoadingSchema] = useState(false)
  const [copied, setCopied] = useState(false)
  const [schemaError, setSchemaError] = useState(false)

  const isEmptyContent = !prompText.trim().length || !generatedSchema.length

  const handlePrompTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setPrompText(e.target.value)
    setSchemaError(false) // Reset error when user types
  }

  const handleGeneratedJSONChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setGeneratedSchema(JSON.parse(e.target.value))
  }

  const handleReset = () => {
    setPrompText('')
    setGeneratedSchema([])
    setSchemaError(false)
    setCopied(false)
  }

  const handleGenerateSchema = async () => {
    if (!currentOrg || !prompText) return

    if (generatedSchema.length > 0) {
      setGeneratedSchema([])
    }

    setLoadingSchema(true)
    setSchemaError(false)

    try {
      const res = await DatabasesAPI.ReqGenDbSchema({
        _Prompt: prompText,
        _OrganizationID: currentOrg?.id,
        _Teams: value.teams.map((tm) => tm.id),
      })
      console.log('Generated Schema JSON:', res)

      // Check if result is empty or null
      if (!res || res.length === 0) {
        setGeneratedSchema([])
        setSchemaError(true)
      } else {
        setGeneratedSchema(res)
        setSchemaError(false)
      }
    } catch (err) {
      console.error('Error generating Schema:', err)
      setSchemaError(true)
    } finally {
      setLoadingSchema(false)
    }
  }

  const handleCopyJSON = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(generatedSchema, null, 2))
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch (err) {
      console.error('Failed to copy:', err)
    }
  }

  const handleOnFinishClose = () => {
    reset()
    setShowModal(false)
  }

  const handleSubmit = async () => {
    if (!currentOrg || !value.db) return
    console.log('Submit generated JSON...', generatedSchema)

    setIsLoading(true)
    setProgressStatus('PROCESSING')

    try {
      // Old endpoint: generate db from schema
      // const res = await DatabasesAPI.ReqCreateDbFromSchema({
      //   _Collections: generatedSchema,
      //   _Name: value.db,
      //   _OrganizationID: currentOrg?.id,
      //   _Teams: value.teams.map((tm) => tm.id),
      // })

      // New endpoint: generate db with content
      const res = await DatabasesAPI.ReqCreateDbWithDataFromSchema({
        _Collections: generatedSchema,
        _Prompt: prompText,
        _Name: value.db,
        _OrganizationID: currentOrg?.id,
        _Teams: value.teams.map((tm) => tm.id),
      })
      console.log('Created database:', res)

      if (res && res._id) {
        setNewDB(res._id)
        setProgressStatus('DONE')
        onEnd?.() // Refresh database list
      }
    } catch (err) {
      console.error('Error creating database:', err)
      setProgressStatus('ERROR')
    } finally {
      setIsLoading(false)
    }
  }

  const handleOnClick = () => {
    if (progressStatus === 'DONE') {
      if (isCreateNewApp && newDB) {
        onCreateApp?.(newDB)
      } else {
        let nextRoute = `${ORG_ROUTE}/${currentOrg?.id}/${ORG_DATABASE_ROUTE}/${newDB}`
        navigate(nextRoute, {
          state: {
            importType: 'makyo-ai',
          },
        })
      }
    }

    if (progressStatus === 'ERROR') {
      // todo: retry
    }

    if (progressStatus === 'INITIAL') {
      return handleSubmit()
    }
  }

  const getButtonLabel = () => {
    if (progressStatus === 'DONE') return t('Open Database')
    if (progressStatus === 'ERROR') return t('Retry')
    if (progressStatus === 'PROCESSING') return t('Loading')
    if (progressStatus === 'INITIAL') return t('Generate Database with Content')
  }

  return (
    <div className='-m-4 h-full flex-1 flex flex-col'>
      <div className='flex-1 p-8'>
        {progressStatus === 'DONE' && (
          <div className='flex flex-col items-center justify-center py-12'>
            <div className='w-20 h-20 mb-5 bg-emerald-100 dark:bg-emerald-900/50 rounded-full flex items-center justify-center animate-in zoom-in duration-500'>
              <MaterialIcon icon='check_circle' size={48} className='text-emerald-600 dark:text-emerald-400' />
            </div>
            <h3 className='text-m-l font-bold text-emerald-900 dark:text-emerald-100 mb-3'>
              Database Created Successfully!
            </h3>
            <p className='text-m-m text-center text-light mb-6 max-w-md leading-relaxed'>
              Your database <span className='font-semibold text-primary'>&quot;{value.db}&quot;</span> has been created
              with the generated schema. Click below to open and start building.
            </p>
            <div className='flex gap-3'>
              {!isCreateNewApp && (
                <button
                  onClick={handleOnFinishClose}
                  className='px-4 py-2 text-m-m font-medium text-light hover:text-primary border border-gray-300 dark:border-gray-600 hover:border-gray-400 dark:hover:border-gray-500 rounded transition-all duration-200 hover:bg-gray-100 dark:hover:bg-gray-800'
                >
                  Close
                </button>
              )}
              <button
                onClick={handleOnClick}
                className='px-4 py-2 text-m-m font-semibold text-white bg-brand hover:bg-brand-700 dark:bg-brand-500 dark:hover:bg-brand-600 rounded transition-all duration-200 shadow-sm hover:shadow'
              >
                {isCreateNewApp && newDB ? 'Create New App' : 'Open Database'}
              </button>
            </div>
          </div>
        )}

        {progressStatus === 'PROCESSING' && (
          <div className='flex flex-col items-center justify-center py-12'>
            <span className='loading spin mb-4' style={{ width: '64px', height: '64px' }} />
            <h3 className='text-m-l font-semibold text-brand-500 mb-2'>Creating Database...</h3>
            <p className='text-m-m text-light'>Please wait while we set up your database.</p>
          </div>
        )}

        {progressStatus === 'ERROR' && (
          <div className='flex flex-col items-center justify-center py-12'>
            <div className='w-16 h-16 mb-4 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center'>
              <MaterialIcon icon='error_outline' size={32} className='text-red-600 dark:text-red-400' />
            </div>
            <h3 className='text-m-l font-bold text-red-900 dark:text-red-100 mb-3'>Failed to Create Database</h3>
            <p className='text-m-m text-center text-red-800 dark:text-red-200 mb-5 max-w-md leading-relaxed'>
              We encountered an error while creating your database. Please try again.
            </p>
            <button
              onClick={() => setProgressStatus('INITIAL')}
              className='px-4 py-2 text-m-m font-semibold text-red-700 dark:text-red-100 hover:text-red-800 dark:hover:text-white border-2 border-red-400 dark:border-red-500 hover:border-red-500 dark:hover:border-red-400 rounded transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/50'
            >
              Try Again
            </button>
          </div>
        )}

        {progressStatus === 'INITIAL' && (
          <section className='w-full mx-auto'>
            <div className='space-y-5'>
              <div className='text-light leading-relaxed'>
                Describe your database schema in plain English. AI will generate a JSON structure that you can review
                and customize before creating your database.
              </div>

              <section>
                <Label defaultSize={false} className='lbl mb-2'>
                  Describe Your Schema
                </Label>
                <div className='mt-1 flex-c p-[1.5px] rounded-sm bg-linear-to-r from-primary-500 via-purple-500 to-pink-500 focus-within:bg-linear-to-br !mb-0'>
                  <textarea
                    value={prompText}
                    onChange={handlePrompTextChange}
                    placeholder='Example: Create a task management system with projects, tasks, and team members. Each task should have a title, description, priority, status, and assignee.'
                    rows={8}
                    className='w-full h-[175px] p-3 rounded-sm border-0 bg-zinc-100 dark:bg-slate-900 text-zinc-800 dark:text-slate-100  focus:outline-hidden focus:ring-0 placeholder:text-zinc-500 dark:placeholder:text-slate-500'
                  />
                </div>
                <div className='mt-3 flex justify-center items-center gap-2'>
                  <Button variant={ButtonVariant.Outlined} onClick={handleReset}>
                    <MaterialIcon icon='refresh' size={16} className='mr-1' />
                    Reset
                  </Button>
                  <Button disabled={!prompText.trim().length || loadingSchema} onClick={handleGenerateSchema}>
                    {loadingSchema ? (
                      <>
                        <span className='loading spin' /> Generating Schema...
                      </>
                    ) : (
                      <>
                        <MaterialIcon icon='auto_awesome' size={16} className='mr-1' />
                        Generate Schema
                      </>
                    )}
                  </Button>
                </div>
              </section>

              {schemaError && (
                <div className='animate-in slide-in-from-top-4 fade-in duration-500'>
                  <hr className='my-4 bdr' />

                  <div className='flex flex-col items-center justify-center p-8 bg-red-50 dark:bg-red-950/40 border-2 border-dashed border-red-400 dark:border-red-600/60 rounded-lg'>
                    <div className='w-16 h-16 mb-4 bg-red-100 dark:bg-red-900/50 rounded-full flex items-center justify-center'>
                      <MaterialIcon icon='error_outline' size={32} className='text-red-600 dark:text-red-400' />
                    </div>
                    <h3 className='text-m-l font-bold text-red-900 dark:text-red-100 mb-3'>
                      Unable to Generate Schema
                    </h3>
                    <p className='text-m-m text-center text-red-800 dark:text-red-200 mb-5 max-w-md leading-relaxed'>
                      We couldn&apos;t generate a valid schema from your description. Please provide a more detailed
                      description of your database structure and try again.
                    </p>
                    <button
                      onClick={() => setSchemaError(false)}
                      className='px-4 py-2 text-m-m font-semibold text-red-700 dark:text-red-100 hover:text-red-800 dark:hover:text-white border-2 border-red-400 dark:border-red-500 hover:border-red-500 dark:hover:border-red-400 rounded transition-all duration-200 hover:bg-red-100 dark:hover:bg-red-900/50'
                    >
                      Try Again
                    </button>
                  </div>
                </div>
              )}

              {generatedSchema.length > 0 && (
                <div className='animate-in slide-in-from-top-4 fade-in duration-500'>
                  <hr className='my-4 bdr' />

                  <section>
                    <div className='flex items-center justify-between mb-2'>
                      <Label defaultSize={false} className='lbl mb-0'>
                        Generated Schema
                      </Label>
                      <button
                        onClick={handleCopyJSON}
                        className='flex items-center gap-1.5 px-3 py-1.5 text-m-xs font-medium text-brand-600 dark:text-brand-400 hover:text-brand-700 dark:hover:text-brand-300 border border-brand-500/30 hover:border-brand-500/60 rounded transition-all duration-200 hover:bg-brand-500/5'
                      >
                        <MaterialIcon icon={copied ? 'check' : 'content_copy'} size={14} />
                        <span>{copied ? 'Copied!' : 'Copy'}</span>
                      </button>
                    </div>
                    <div className='mt-1 flex-c p-[1.5px] rounded-sm border-2 border-dashed border-brand-500/30 focus-within:border-brand-500/60 !mb-0'>
                      <textarea
                        value={JSON.stringify(generatedSchema, null, 2)}
                        onChange={handleGeneratedJSONChange}
                        rows={8}
                        className='w-full h-[300px] p-3 rounded-sm border-0 font-mono text-m-m bg-zinc-100 dark:bg-gray-900 text-zinc-800 dark:text-gray-100 focus:outline-hidden focus:ring-0'
                      />
                    </div>
                    <div className='mt-2 flex items-start gap-2 text-m-xs text-light'>
                      <MaterialIcon icon='info' size={16} className='flex-shrink-0 mt-0.5 opacity-60' />
                      <span>You can edit the JSON directly to customize the schema before proceeding.</span>
                    </div>
                  </section>
                </div>
              )}
            </div>
          </section>
        )}
      </div>

      {progressStatus === 'INITIAL' && (
        <Modal.Footer className='justify-between'>
          <Button
            variant={ButtonVariant.Outlined}
            disabled={isLoading}
            onClick={() => {
              setStep(step - 1)
              if (step === 1) {
                reset()
              }
            }}
          >
            <Trans>Back</Trans>
          </Button>

          <SaveButton
            isLoading={isLoading}
            onClick={handleOnClick}
            label={getButtonLabel()}
            disabled={isEmptyContent}
          />
        </Modal.Footer>
      )}
    </div>
  )
}

/**
 * New Create DB with Makyo AI Modal
 *
 * @todo:
 * - [ ] Add upload files limit (5 files max)
 * - [ ] Add file type limit (pdf, docx, txt, etc)
 * - [ ] Add file size limit (10MB max)
 */
function ContentToDatabase({ onCreateApp, onEnd }: { onCreateApp?: (dbId: string) => void; onEnd?: () => void }) {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const { currentOrg } = useCurrentOrgContext()
  const { setStep, step, value, reset, setShowModal, isLoading, setIsLoading } = useCreateDbContext()

  const [newDB, setNewDB] = useState<string | undefined>(undefined)
  const [progressStatus, setProgressStatus] = useState<'INITIAL' | 'PROCESSING' | 'DONE' | 'ERROR'>('INITIAL')
  const [eventProgress, setEventProgress] = useState<Map<string, EventProgress>>(new Map())

  const [provider, setProvider] = useState(AI_PROVIDERS.gemini.value)
  const [model, setModel] = useState(AI_PROVIDERS.gemini.models[0])
  const [temperature, setTemperature] = useState(1)
  const [topK, setTopK] = useState(40.0)
  const [topP, setTopP] = useState(0.95)
  const [instructionsText, setInstructionsText] = useState('')
  const [files, setFiles] = useState<File[]>([])

  const fileInputRef = useRef<HTMLInputElement>(null)

  const isEmptyContent = !instructionsText.trim().length && files.length === 0

  const handleInstructionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setInstructionsText(e.target.value)
  }

  const handleUploadFiles = () => {
    fileInputRef.current?.click()
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFiles = Array.from(e.target.files || [])
    setFiles((prev) => [...prev, ...selectedFiles])
  }

  const handleRemoveFile = (fileToRemove: File) => {
    setFiles((prev) => prev.filter((file) => file !== fileToRemove))
  }

  const handleSubmit = async () => {
    if (!currentOrg) return

    setIsLoading(true)

    try {
      // Parse each file into a string using Promise.all
      const fileContents = await Promise.all(
        files.map((file) => {
          return new Promise<string>((resolve, reject) => {
            const reader = new FileReader()

            reader.onload = (event) => {
              const content = event.target?.result as string
              resolve(`File: ${file.name}\n${content}\n\n`)
            }

            reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`))
            reader.readAsText(file)
          })
        })
      )

      // Join all file contents together
      const allContent = fileContents.join('\n')

      // Combine instructions with file contents
      const finalContent = `Instructions:\n${instructionsText}\n\nContent:\n\n${allContent}`

      // Generate payload
      const payload: IGenerateDatabaseBody = {
        _Platform: provider,
        _Model: model,
        _Options: {
          temperature,
        },
        _Content: finalContent,
        _Name: value.db,
        _Teams: value.teams.map((tm) => tm.id),
        _OrganizationID: currentOrg?.id,
        _SampleData: false,
        _IsExternalDB: false,
        _ExternalDB: {
          _OrgPluginID: value.pluginRefId ?? '',
          _Synced: false,
          _Syncing: false,
          _LastSyncAt: new Date(),
        },
      }

      console.log('Generate DB with AI...', payload)

      // Create database
      await DatabasesAPI.ReqGenerateDatabase(payload, (event, status) => {
        console.log({ eventName: event.event, eventData: event.data, status })

        const eventName = event.event
        const eventData = event.data
        const eventStatus = status

        if (eventName === 'INIT') {
          setProgressStatus('PROCESSING')
        } else if (eventName === 'DONE') {
          onEnd?.() // fetch database list
          setProgressStatus('DONE')
        } else if (eventName === 'DB') {
          const dbID = eventData.id
          setNewDB(dbID)
        } else {
          setEventProgress((prev) => {
            // check if event name contains `_DONE` at the end, if so, remove it, for example:
            // GET_SCHEMA_DONE -> GET_SCHEMA
            // GET_DATA_PROGRESS -> GET_DATA
            if (eventName.endsWith('_DONE')) {
              const cleanName = eventName.slice(0, -5)
              prev.set(cleanName, {
                eventName: cleanName,
                status: 'DONE',
              })
            } else if (eventName.endsWith('_PROGRESS')) {
              const cleanName = eventName.slice(0, -9)
              prev.set(cleanName, {
                eventName: cleanName,
                status: eventData.progress === 100 ? 'DONE' : 'WORKING',
                progress: eventData.progress,
              })
            } else {
              prev.set(eventName, {
                eventName: eventName,
                status: eventStatus,
              })
            }

            return prev
          })
        }
      })
    } catch (error) {
      console.error('Error reading files:', error)
      setProgressStatus('ERROR')
      // todo: error handling UI
    } finally {
      setIsLoading(false)
    }
  }

  const handleOnClick = () => {
    if (progressStatus === 'DONE') {
      let nextRoute = `${ORG_ROUTE}/${currentOrg?.id}/${ORG_DATABASE_ROUTE}/${newDB}`
      navigate(nextRoute, {
        state: {
          importType: 'makyo-ai',
        },
      })
    }

    if (progressStatus === 'ERROR') {
      // todo: retry
    }

    if (progressStatus === 'INITIAL') {
      return handleSubmit()
    }
  }

  const handleOnFinishClose = () => {
    reset()
    setShowModal(false)
  }

  const getButtonLabel = () => {
    if (progressStatus === 'DONE') return t('Open Database')
    if (progressStatus === 'ERROR') return t('Retry')
    if (progressStatus === 'PROCESSING') return t('Loading')
    if (progressStatus === 'INITIAL') return t('Next')
  }

  return (
    <div className='-m-4 h-full flex-1 flex flex-col'>
      <div className='p-8'>
        {(progressStatus === 'PROCESSING' || progressStatus === 'DONE') && (
          <div className='flex-1 w-full mx-auto'>
            <div className='flex-c'>
              <div className='font-medium'>
                <Trans>Generating Database</Trans>
              </div>
            </div>

            <div className='space-y-4 my-6' id='create-db-progress-container'>
              {Array.from(eventProgress.entries()).map(([eventName, evtData], index) => {
                return (
                  <div key={index} className='space-y-2'>
                    <div className='flex justify-between items-center'>
                      <div>{EVENT_PROGRESS_LABEL[eventName] ?? eventName}</div>
                      <div
                        className={clsx('flex items-center', {
                          'text-emerald-500': evtData.status === 'DONE',
                          'text-red-500': evtData.status === 'ERROR',
                        })}
                      >
                        {evtData.status === 'DONE' && <Check16 width={20} height={20} className='mr-2' />}
                        {evtData.status === 'WORKING' && (
                          <div className='ml-2 h-4 w-4 animate-spin rounded-full border-3 border-solid border-t-transparent mr-2'></div>
                        )}
                        {evtData.status === 'ERROR' && (
                          <Info16 width={16} height={16} className='text-red-600 dark:text-red-400 mr-2' />
                        )}
                      </div>
                    </div>
                    <div className='h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden'>
                      <div
                        className={clsx(
                          `h-full ${STATUS_COLORS[evtData.status as keyof typeof STATUS_COLORS]} rounded-full transition-all duration-300`,
                          {
                            'animate-progress-indeterminate':
                              evtData.status === 'WORKING' && evtData.progress === undefined,
                          }
                        )}
                        style={{
                          width:
                            evtData.status === 'DONE'
                              ? '100%'
                              : typeof evtData.progress === 'number'
                                ? `${evtData.progress}%`
                                : '0%',
                        }}
                      />
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {progressStatus === 'ERROR' && (
          <div className='flex-1 w-full mx-auto'>
            <div className='flex-c'>
              <div className='font-medium'>Error</div>
            </div>
          </div>
        )}

        {progressStatus === 'INITIAL' && (
          <section className='w-full mx-auto'>
            <div className='space-y-4'>
              <div>
                Enter your content or upload up to 5 files. Makyo AI will transform your content into a database table,
                allowing you to build your app or website effortlessly.
              </div>

              <div>
                <Field>
                  <Label defaultSize={false} className='lbl'>
                    {t('Provider')}
                  </Label>
                  <SelectDropdown
                    options={Object.values(AI_PROVIDERS).map((p) => p.value)}
                    renderOption={(v) => AI_PROVIDERS[v].label ?? ''}
                    value={provider}
                    onChange={(v) => {
                      setProvider(v)
                      if (provider !== v) {
                        setModel(AI_PROVIDERS[v].models[0])
                      }
                    }}
                  />
                </Field>

                <Field>
                  <Label defaultSize={false} className='lbl'>
                    {t('Model')}
                  </Label>
                  <SelectDropdown
                    withSearch
                    options={AI_PROVIDERS[provider].models}
                    value={model}
                    onChange={(v) => setModel(v)}
                  />
                </Field>

                <Field>
                  <Label defaultSize={false} className='lbl'>
                    {t('Temperature')}
                  </Label>
                  <Slider
                    values={[temperature]}
                    step={0.1}
                    min={0}
                    max={2}
                    renderLabel={(v) => v}
                    renderTrackLabel={(v) => v}
                    onChange={(value) => {
                      setTemperature(value[0])
                    }}
                  />
                </Field>

                {/* <Field>
                <Label>{t('Top P')}</Label>
                <SimpleInput
                  value={topP}
                  onChange={(e) => setTopP(parseFloat(e.target.value))}
                  placeholder=''
                  type='number'
                  step={0.01}
                />
              </Field>

              <Field>
                <Label>{t('Top K')}</Label>
                <SimpleInput
                  value={topK}
                  onChange={(e) => setTopK(parseFloat(e.target.value))}
                  placeholder=''
                  type='number'
                  step={0.1}
                />
              </Field> */}
              </div>

              <div className='flex-c p-[1.5px] rounded-sm bg-linear-to-r from-primary-500 via-purple-500 to-pink-500 focus-within:bg-linear-to-br !mb-0'>
                <textarea
                  value={instructionsText}
                  onChange={handleInstructionsChange}
                  placeholder='Describe the content you want to create'
                  rows={8}
                  className='w-full p-2 rounded-sm border-0 overflow-hidden bg-zinc-100 dark:bg-slate-900 text-zinc-800 dark:text-slate-100  focus:outline-hidden focus:ring-0'
                />
              </div>
              <input type='file' ref={fileInputRef} onChange={handleFileChange} className='hidden' multiple />
            </div>

            <div className='flex flex-col gap-4 mt-3 mx-1'>
              {files.length > 0 && (
                <div className='p-1'>
                  <div className='space-y-2'>
                    <div className='text-m-sm font-medium text-light'>Attached Files:</div>
                    <div className='space-y-2'>
                      {files.map((file, index) => (
                        <div
                          key={index}
                          className='flex items-center justify-between py-2 px-3 bg-zinc-100 dark:bg-slate-900 rounded-sm'
                        >
                          <span className='truncate'>{file.name}</span>
                          <button
                            onClick={() => handleRemoveFile(file)}
                            className='text-red-600 dark:text-red-400 hover:text-red-500 dark:hover:text-red-500'
                          >
                            <Trash16 />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              <div className='flex justify-start'>
                <Button
                  variant={ButtonVariant.Outlined}
                  defaultSize={false}
                  onClick={handleUploadFiles}
                  icon={<Upload16 />}
                >
                  Upload Files
                </Button>
              </div>
            </div>
          </section>
        )}
      </div>

      <Modal.Footer className='justify-between'>
        <Button
          variant={ButtonVariant.Outlined}
          disabled={isLoading}
          onClick={() => {
            if (progressStatus === 'DONE') {
              handleOnFinishClose()
            } else {
              setStep(step - 1)
              if (step === 1) {
                reset()
              }
            }
          }}
        >
          <Trans>{progressStatus === 'DONE' ? 'Close' : 'Back'}</Trans>
        </Button>

        <SaveButton isLoading={isLoading} onClick={handleOnClick} label={getButtonLabel()} disabled={isEmptyContent} />
      </Modal.Footer>
    </div>
  )
}

export function CreateDbMakyoAI({
  type,
  onEnd,
  onCreateApp,
}: {
  type: 'content-to-database' | 'prompt-to-json'
  onEnd?: () => void
  onCreateApp?: (dbId: string) => void
}) {
  if (type === 'content-to-database') {
    return <ContentToDatabase onCreateApp={onCreateApp} onEnd={onEnd} />
  }

  if (type === 'prompt-to-json') {
    return <PromptToJson onCreateApp={onCreateApp} onEnd={onEnd} />
  }

  return null
}
